import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@modules/iam';
import { validateLoginInput } from '../domain/password';
import { apiErrorMessage } from './apiError';

export interface LoginResult {
  forcePasswordChange: boolean;
}

/**
 * UC-1: valida localmente (vacíos tras trim), delega el login a `@modules/iam` y
 * devuelve si el backend exige cambio de contraseña — la navegación (a `/change-password` o al
 * `from` original) queda en `presentation/`, que es quien conoce `useLocation`/`useNavigate`.
 */
export function useLogin() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = useCallback(
    async (username: string, password: string): Promise<LoginResult | null> => {
      if (!validateLoginInput(username, password)) {
        setError(t('auth.usernameAndPasswordRequired'));
        return null;
      }
      setLoading(true);
      setError('');
      try {
        const data = await login(username, password);
        return { forcePasswordChange: Boolean(data.user?.forcePasswordChange) };
      } catch (err: unknown) {
        setError(apiErrorMessage(err) || t('auth.loginError'));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [login, t]
  );

  return { error, loading, submit, clearError: () => setError('') };
}
